import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import {
  AboSettingsAuditLog,
  AboTopicHasPostingsResponse,
  MediaFileMetadata,
  TopicsWithMetadataResponse,
  UserAboSettings,
} from '@3m5/sm_publish-frontend-api/models'

import { ApiError } from '../../store/topicRoot.reducer'
import {
  acknowledgeSettingsForNetworkPage,
  createAboTopic, deleteAboTopic, getAboSettingsForCurrentUser, getAboTopicHasPostings, getAuditLogForNetworkPage,
  getTopicsAndMetadataForCurrentUser, updateAboSettingsForCurrentUser, updateAboTopic, uploadTemporaryMediaFile,
} from './topic.actions'

export type AboHasPostingsObject = {
  [key:string]: AboTopicHasPostingsResponse
}

export interface TopicState {
  fetching: boolean
  reNewSettings: boolean
  uploadPending: boolean
  aboTopics?: TopicsWithMetadataResponse
  aboSettings?: UserAboSettings[]
  temporaryFileMetadata?: MediaFileMetadata
  aboHasPostings: AboHasPostingsObject
  error: ApiError
  auditLogForCurrentUser?: AboSettingsAuditLog[]
}

const INITIAL_STATE: TopicState = {
  fetching: false,
  reNewSettings: false,
  uploadPending: false,
  aboHasPostings: {},
  error: {},
}

export const TopicReducer = reducerWithInitialState(INITIAL_STATE)
  /**
   * createAboTopic
   */
  .case(createAboTopic.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.createAboTopic
    })
  })

  .case(createAboTopic.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.temporaryFileMetadata = undefined
    })
  })

  .case(createAboTopic.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.createAboTopic = (error as Response)
    })
  })

  /**
   * deleteAboTopic
   */
  .case(deleteAboTopic.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      // draft.aboTopicStatus = AboTopicStatus.pending
      delete draft.error.deleteAboTopic
    })
  })

  .case(deleteAboTopic.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      // draft.aboTopicStatus = AboTopicStatus.successDelete
    })
  })

  .case(deleteAboTopic.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteAboTopic = (error as Response)
    })
  })

  /**
   * getTopicsAndMetadataForCurrentUser
   */
  .case(getTopicsAndMetadataForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getTopicsAndMetadataForCurrentUser
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboTopics = result
      draft.fetching = false
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getTopicsAndMetadataForCurrentUser = (error as Response)
    })
  })

  /**
   * updateAboTopic
   */
  .case(updateAboTopic.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateAboTopic
    })
  })

  .case(updateAboTopic.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.temporaryFileMetadata = undefined
    })
  })

  .case(updateAboTopic.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateAboTopic = (error as Response)
    })
  })

  /**
   * getAboSettingsForCurrentUser
   */
  .case(getAboSettingsForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboSettingsForCurrentUser
    })
  })

  .case(getAboSettingsForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboSettings = result
      draft.fetching = false
      draft.reNewSettings = false
    })
  })

  .case(getAboSettingsForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAboSettingsForCurrentUser = (error as Response)
    })
  })

  /**
   * updateAboSettingsForCurrentUser
   */
  .case(updateAboSettingsForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateAboSettingsForCurrentUser
    })
  })

  .case(updateAboSettingsForCurrentUser.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reNewSettings = true
    })
  })

  .case(updateAboSettingsForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateAboSettingsForCurrentUser = (error as Response)
    })
  })

  /**
   * uploadTemporaryMediaFile
   */
  .case(uploadTemporaryMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.uploadPending = true
      delete draft.error.uploadTemporaryMediaFile
    })
  })

  .case(uploadTemporaryMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.temporaryFileMetadata = result
      draft.uploadPending = false
      draft.fetching = false
    })
  })

  .case(uploadTemporaryMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.uploadPending = false
      draft.error.uploadTemporaryMediaFile = (error as Response)
    })
  })

  /**
   * getAboTopicHasPostings
   */
  .case(getAboTopicHasPostings.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboTopicHasPostings
    })
  })

  .case(getAboTopicHasPostings.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboHasPostings[result.topicId] = result
      draft.fetching = false
    })
  })

  .case(getAboTopicHasPostings.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAboTopicHasPostings = (error as Response)
    })
  })

  /**
   * getAuditLogForCurrentUser
   */
  .case(getAuditLogForNetworkPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAuditLogForCurrentUser
    })
  })

  .case(getAuditLogForNetworkPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.auditLogForCurrentUser = result
      draft.fetching = false
    })
  })

  .case(getAuditLogForNetworkPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAuditLogForCurrentUser = (error as Response)
    })
  })

  /**
   * acknowledgeSettingsForCurrentUser
   */
  .case(acknowledgeSettingsForNetworkPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.acknowledgeSettingsForCurrentUser
    })
  })

  .case(acknowledgeSettingsForNetworkPage.done, (state) => {
    return produce(state, draft => {
      // draft.reNewSettings = true
      draft.aboHasPostings = {}
      draft.fetching = false
    })
  })

  .case(acknowledgeSettingsForNetworkPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.acknowledgeSettingsForCurrentUser = (error as Response)
    })
  })
